import type { VorgangDto } from "../services/vorgangService";
import { bearbeiterKeys } from "../utils/vorgangScope";
import type { AppUser } from "../services/userService";

/** Reine Anzeige-/Ableitungs-Helfer des Kontakt-Vorgänge-Tabs (SP2) — separat
 *  gehalten, damit sie ohne Fluent-UI-Ballast unit-testbar sind. */

/** ISO-Datum → 'DD.MM.YYYY' (de-DE); null/leer → „—". */
export const fmtDate = (iso?: string | null): string =>
    iso ? new Date(iso).toLocaleDateString("de-DE") : "—";

/** TWV = Datum + optionale Uhrzeit ('HH:MM'); ohne twv → „—". */
export const fmtTwv = (v: Pick<VorgangDto, "twv" | "twvUhrzeit">): string => {
    if (!v.twv) return "—";
    const d = new Date(v.twv).toLocaleDateString("de-DE");
    return v.twvUhrzeit ? `${d} ${v.twvUhrzeit}` : d;
};

/** Kürzel (max. 2 Zeichen) für den Avatar eines Beteiligten. */
export const initialsFor = (user: AppUser | undefined, fallback: string): string => {
    if (user) {
        const f = (user.firstName || "").trim();
        const l = (user.lastName || "").trim();
        if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
        const nm = (user.name || user.username || user.email || "").trim();
        if (nm) {
            const parts = nm.split(/[\s,]+/).filter(Boolean);
            if (parts.length >= 2) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
            return nm.slice(0, 2).toUpperCase();
        }
    }
    return fallback.replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase() || "?";
};

/** SP3 (A10): Badge-Text „über <Firma>" für Vorgänge, die über eine verbundene
 *  Firma (Ansprechpartner-Beziehung) beim Kontakt erscheinen; sonst null. */
export const viaCompanyLabel = (
    v: Pick<VorgangDto, "viaCompany">,
): string | null => {
    if (!v.viaCompany) return null;
    const name = (v.viaCompany.name ?? "").trim();
    return name ? `über ${name}` : "über Firma";
};

/** Beteiligte-ids eines Vorgangs — Reihenfolge Bearbeiter → Hauptverantwortliche
 *  → zu Informierende, dedupliziert (Bearbeiter behalten Vorrang). */
export const participantIds = (
    v: Pick<VorgangDto, "bearbeiter" | "hauptverantwortlich" | "zuInformierende">,
): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    const push = (arr?: Array<string | null | undefined>) => {
        for (const x of arr ?? []) {
            if (x && !seen.has(x)) {
                seen.add(x);
                out.push(x);
            }
        }
    };
    push(bearbeiterKeys(v.bearbeiter));
    push(v.hauptverantwortlich);
    push(v.zuInformierende);
    return out;
};
